import { Injectable } from "@angular/core";
import { Store } from "@karma-games/angular-devkit/src/lib/extended-store";
import {
    BehaviorSubject,
    catchError,
    filter,
    firstValueFrom,
    lastValueFrom,
    Observable,
    race,
    Subject, switchMap,
    throwError,
    timeout
} from "rxjs";
import {
    EBetSplit,
    ERoundState,
    IBetActionResponse,
    IRound,
    IRoundBet,
    IRoundBetAccepted,
    IRoundBetCanceled,
    IRoundBetsAcceptedPayload,
    IRoundBetsCanceledPayload,
    IRoundCashOut,
    IRoundEndPayload,
    IRoundInitialState,
    IRoundPayout,
    IRoundProgressPayload,
    IRoundRepository,
    IRoundGetStateData,
    TBetActionResponse, IRoundBetFailed, IRoundCashOutFailed,
} from "./interfaces";
import {
    addBets,
    addRound,
    addUserBet,
    changeState,
    clearRound,
    firstHalfCashedOut,
    initialStateUpdate,
    removeAutoplayOptions,
    removeBets,
    removeUserBet,
    setAutoplayOptions,
    updateBets,
    updateMultiplier,
    userBetConfirmed
} from "./store/round.actions";
import { buildRoundBetId } from "./round-bet-id.builder";
import { CurrencyService } from "../../shared/services/currency.service";
import { IAutoplayOptions, IUserBet } from "./store/interfaces";
import { ICommunication, SocketService } from "@karma-games/angular-devkit/src/lib/services/communication";
import { map, take, takeUntil } from "rxjs/operators";
import { IUser, updateUser } from "@karma-games/angular-devkit/src/lib/modules/user";
import { BetCancelFailedError, BetCashOutFailedError, BetFailedError } from "../domain-errors";
import { Dictionary } from "lodash";
import { DomainErrors, ErrorFactory } from "@karma-games/angular-devkit/src/lib/classes/errors";
import BetsNoLongerAccepted = DomainErrors.BetsNoLongerAccepted;
import UnsufficientBalanceError = DomainErrors.UnsufficientBalanceError;
import StopReceivedAfterGameEnd = DomainErrors.StopReceivedAfterGameEnd;
import BetDoesntExist = DomainErrors.BetDoesntExist;
import BetAlreadyExists = DomainErrors.BetAlreadyExists;
import { Action } from "@ngrx/store";

@Injectable()
export class RoundRepository implements IRoundRepository {
    protected gameNsp: ICommunication;
    protected destroy$: Subject<void> = new Subject();
    protected cashOut$: Subject<IRoundCashOut> = new Subject();
    protected betActionInProgress$: BehaviorSubject<Dictionary<boolean>> = new BehaviorSubject({
        0: false,
        1: false
    });
    protected betActionResult$: Subject<IBetActionResponse<IRoundBetAccepted | IRoundBetFailed>> = new Subject<IBetActionResponse<IRoundBetAccepted | IRoundBetFailed>>();
    protected payoutActionResultOnFirstBetIndex$: BehaviorSubject<IBetActionResponse<IRoundCashOut> | undefined> = new BehaviorSubject(undefined);
    protected payoutActionResultOnSecondBetIndex$: BehaviorSubject<IBetActionResponse<IRoundCashOut> | undefined> = new BehaviorSubject(undefined);

    constructor(private store: Store,
                private socket: SocketService,
                private currencyService: CurrencyService) {
        this.gameNsp = this.socket.connectToNamespace("game", {
            keepAliveIntervalInMilliseconds: 1000,
            serverTimeoutInMilliseconds: 2000,
        });
        this.gameNsp.on<any>("startIdle")
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: { roundId: string }) => {
                this.store.dispatch(clearRound());
                this.store.dispatch(addRound({
                    id: data.roundId
                }));
            });
        this.gameNsp.on<any>("betsAccepted")
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: IRoundBetsAcceptedPayload) => {
                if (data.payload.bets.length) {
                    console.warn("betsAccepted", data);
                    this.store.dispatch(addBets({
                        bets: data.payload.bets.map(bet => {
                            const player: IUser = {
                                id: bet.player.id,
                                name: bet.player.name,
                                currency: bet.currency,
                                avatarUrl: bet.player.avatar,
                                language: "",
                                canEditName: false
                            };
                            return {
                                id: buildRoundBetId(player.id, bet.betIndex),
                                user: player,
                                bet: this.currencyService.convertCurrencyAmount(bet.amount, bet.currency)
                            };
                        }),
                        totalBets: data.payload.totalBets
                    }));
                }
            });
        this.gameNsp.on<any>("betsCanceled")
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: IRoundBetsCanceledPayload) => {
                if (data.payload.bets.length) {
                    console.warn("betsCanceled", data);
                    this.store.dispatch(removeBets({
                        ids: data.payload.bets.map(bet => {
                            return buildRoundBetId(bet.playerId, bet.betIndex);
                        }),
                        totalBets: data.payload.totalBets
                    }));
                }
            });
        this.gameNsp.on<any>("startGame")
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.store.dispatch(changeState({ state: ERoundState.Playing }));
            });
        this.gameNsp.on<any>("progress")
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: {
                roundId: string,
                payload: IRoundProgressPayload
            }) => {
                this.store.dispatch(changeState({ state: ERoundState.Playing }));
                if (data.payload.payouts.length) {
                    console.warn("progress payouts", data);
                    this.store.dispatch(updateBets({
                        bets: data.payload.payouts.map((p: IRoundPayout) => {
                            return {
                                id: buildRoundBetId(p.playerId, p.betIndex),
                                multiplier: p.multiplierValue,
                                cashout: this.currencyService.convertCurrencyAmount(p.amount, p.currency),
                                split: p.split,
                                totalAmount: p.totalAmount
                            };
                        })
                    }));
                }
                this.store.dispatch(updateMultiplier({
                    multiplier: data.payload.multiplier
                }));
            });
        this.gameNsp.on<any>("endGame")
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: IRoundEndPayload) => {
                if (data.payload.payouts.length) {
                    console.warn("endGame payouts", data);
                    this.store.dispatch(updateBets({
                        bets: data.payload.payouts.map((p: IRoundPayout) => {
                            return {
                                id: buildRoundBetId(p.playerId, p.betIndex),
                                multiplier: p.multiplierValue,
                                cashout: this.currencyService.convertCurrencyAmount(p.amount, p.currency),
                                split: p.split,
                                totalAmount: p.totalAmount
                            };
                        })
                    }));
                }
                this.store.dispatch(changeState({ state: ERoundState.Ended, multiplier: data.payload.multiplier }));
            });

        this.gameNsp.on<any>("betAccepted")
            .pipe(map((event) => {
                if (event && (<any>event).error) {
                    return this.mapBetActionRpcResponse<IRoundBetFailed>({
                        error: ErrorFactory.createDomainError((<any>event).error, (<any>event).data ?? (<any>event).payload),
                        data: (<any>event).data ?? (<any>event).payload
                    }, false);
                } else {
                    return this.mapBetActionRpcResponse<IRoundBetAccepted>({
                        ...event.payload,
                        roundId: event.roundId
                    }, true);
                }
            }))
            .subscribe((data: IBetActionResponse<IRoundBetAccepted | IRoundBetFailed>) => {
                this.betActionResult$.next(data);
            });
        this.gameNsp.on<any>("betFailed")
            .pipe(map((event) => {
                if (event && (<any>event).error) {
                    return this.mapBetActionRpcResponse<IRoundBetFailed>({
                        error: ErrorFactory.createDomainError((<any>event).error, (<any>event).data ?? (<any>event).payload),
                        data: (<any>event).data ?? (<any>event).payload
                    }, false);
                } else {
                    return this.mapBetActionRpcResponse<IRoundBetAccepted>({
                        ...event.payload,
                        roundId: event.roundId
                    }, true);
                }
            }))
            .subscribe((data: IBetActionResponse<IRoundBetAccepted | IRoundBetFailed>) => {
                this.betActionResult$.next(data);
            });

        this.gameNsp.on<any>("payoutSuccess")
            .pipe(map((res) => this.mapBetActionRpcResponse<IRoundCashOut>({
                ...res.payload,
                roundId: res.roundId
            }, true)))
            .subscribe((data: IBetActionResponse<IRoundCashOut>) => {
                if (data.response.betIndex === 0) {
                    this.payoutActionResultOnFirstBetIndex$.next(data);
                } else {
                    this.payoutActionResultOnSecondBetIndex$.next(data);
                }
                this.cashOut$.next(data.response);
                if (data.response.split !== EBetSplit.FirstHalf) {
                    this.removeUserBet(data.response.betIndex, true, false);
                }
                this.betActionInProgress$.next({
                    ...this.betActionInProgress$.getValue(),
                    [data.response.betIndex]: false
                });
            });
        this.gameNsp.on<any>("payoutFailed")
            .pipe(map((event) => {
                if (event && (<any>event).error) {
                    return this.mapBetActionRpcResponse<IRoundCashOutFailed>({
                        error: ErrorFactory.createDomainError((<any>event).error, (<any>event).data ?? (<any>event).payload),
                        data: (<any>event).data ?? (<any>event).payload
                    }, false);
                } else {
                    return this.mapBetActionRpcResponse<IRoundCashOut>({
                        ...event.payload,
                        roundId: event.roundId
                    }, true);
                }
            }))
            .subscribe((data: IBetActionResponse<IRoundCashOut | IRoundCashOutFailed>) => {
                const betIdx: number = (<IBetActionResponse<IRoundCashOut>>data).response.betIndex || (<IBetActionResponse<IRoundCashOutFailed>>data).response.data.betIndex;
                if (betIdx === 0) {
                    if ((<IBetActionResponse<IRoundCashOutFailed>>data).response.error) {
                        this.payoutActionResultOnFirstBetIndex$.error((<IBetActionResponse<IRoundCashOutFailed>>data).response.error);
                    } else {
                        this.payoutActionResultOnFirstBetIndex$.next(<IBetActionResponse<IRoundCashOut>>data);
                    }
                } else if (betIdx === 1) {
                    if ((<IBetActionResponse<IRoundCashOutFailed>>data).response.error) {
                        this.payoutActionResultOnSecondBetIndex$.error((<IBetActionResponse<IRoundCashOutFailed>>data).response.error);
                    } else {
                        this.payoutActionResultOnSecondBetIndex$.next(<IBetActionResponse<IRoundCashOut>>data);
                    }
                }
            });
    }

    private subscribeBetActionResult(betIndex: number): Observable<IBetActionResponse<IRoundBetAccepted | IRoundBetFailed>> {
        return this.betActionResult$.asObservable().pipe(filter((d: IBetActionResponse<IRoundBetAccepted | IRoundBetFailed>) => {
            if ((<IBetActionResponse<IRoundBetAccepted>>d).response?.betIndex) {
                return (<IBetActionResponse<IRoundBetAccepted>>d).response.betIndex === betIndex;
            } else if ((<IBetActionResponse<IRoundBetFailed>>d).response?.data?.betIndex) {
                return (<IBetActionResponse<IRoundBetFailed>>d).response.data.betIndex === betIndex;
            } else {
                return true;
            }
        }), switchMap(res => new Observable<IBetActionResponse<IRoundBetAccepted | IRoundBetFailed>>(subscriber => {
            if ((<IBetActionResponse<IRoundBetFailed>>res).response.error) {
                subscriber.error((<IBetActionResponse<IRoundBetFailed>>res).response.error);
            } else {
                subscriber.next(<IBetActionResponse<IRoundBetAccepted>>res);
            }
        })));
    }

    private subscribeCashOutActionResult(betIndex: number, roundId: string): Observable<IBetActionResponse<IRoundCashOut> | undefined> {
        if (betIndex === 0) {
            return this.payoutActionResultOnFirstBetIndex$.asObservable().pipe(catchError((e) => {
                    return throwError(e);
                }),
                filter(d => !!d && d.response.betIndex === betIndex && d?.response.roundId === roundId));
        } else {
            return this.payoutActionResultOnSecondBetIndex$.asObservable().pipe(catchError((e) => {
                    return throwError(e);
                }),
                filter(d => !!d && d.response.betIndex === betIndex && d?.response.roundId === roundId));
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    get(): Observable<IRound> {
        return this.store.subscribeRound();
    }

    getRoundBets(): Observable<IRoundBet[]> {
        return this.store.subscribeRoundBets();
    }

    getCurrentRoundState(): Observable<IRoundGetStateData | undefined> {
        return this.store.subscribeRoundInitialState();
    }

    async fetchCurrentRoundState(): Promise<IRoundGetStateData> {
        try {
            const response: IRoundGetStateData = await this.gameNsp.rpc("getState");
            const mappedData: IRoundInitialState = {
                userBets: response.bets.map(bet => {
                    const userBet: IUserBet = {
                        amount: bet.amount,
                        betIndex: bet.betIndex,
                        maxMultiplier: bet.autoStopMultiplier,
                        accumulatedValue: bet.amount,
                        multiplier: 1,
                        roundId: response.roundId,
                        firstHalfCashedOut: undefined
                    };
                    return userBet;
                }),
                bets: response.betsAccepted.map(bet => {
                    const player: IUser = {
                        id: bet.player.id,
                        name: bet.player.name,
                        currency: bet.currency,
                        avatarUrl: bet.player.avatar,
                        language: "",
                        canEditName: false
                    };
                    const payout: IRoundPayout | undefined = response.payouts.find((p: IRoundPayout) => p.playerId === player.id && p.betIndex === bet.betIndex);
                    return !payout ? {
                        id: buildRoundBetId(player.id, bet.betIndex),
                        user: player,
                        bet: this.currencyService.convertCurrencyAmount(bet.amount, bet.currency),
                    } : {
                        id: buildRoundBetId(player.id, bet.betIndex),
                        user: player,
                        bet: this.currencyService.convertCurrencyAmount(bet.amount, bet.currency),
                        multiplier: payout.multiplierValue,
                        cashout: this.currencyService.convertCurrencyAmount(payout.amount, payout.currency),
                        split: payout.split,
                        totalAmount: payout.totalAmount
                    };
                }),
                roundId: response.roundId,
                totalBets: response.totalBets,
                initialRoundState: response
            };
            this.store.dispatch(initialStateUpdate({ data: mappedData }));
            this.store.dispatch(<Action>updateUser({ balance: response.balance }));
            return response;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    clear(): void {
        this.store.dispatch(clearRound());
    }

    subscribeBetActionInProgress(betIndex: number): Observable<boolean> {
        return this.betActionInProgress$.pipe(map(progressStates => progressStates[betIndex]));
    }

    private mapBetActionRpcResponse<T = TBetActionResponse>(response: T, successful: boolean): {
        response: T;
        successful: boolean;
    } {
        return { response, successful };
    };

    sendBet = async (amount: number, betIndex: number, currency: string, maxMultiplier: number | undefined): Promise<void> => {
        if (this.betActionInProgress$.getValue()[betIndex]) {
            return;
        }
        try {
            this.betActionInProgress$.next({
                ...this.betActionInProgress$.getValue(),
                [betIndex]: true
            });
            this.removeUserBet(betIndex, false, true);
            this.addUserBet(amount, betIndex, maxMultiplier);
            await this.gameNsp.initialized;
            await this.gameNsp.rpc("bet", {
                payload: {
                    betIndex,
                    amount,
                    currency,
                    autoStopMultiplier: maxMultiplier
                }
            });
            const res: IBetActionResponse<IRoundBetAccepted | IRoundBetFailed> = await lastValueFrom(this.subscribeBetActionResult(betIndex).pipe(
                timeout(120000), take(1)));
            if (res.successful) {
                this.store.dispatch(userBetConfirmed({
                    roundId: (<IBetActionResponse<IRoundBetAccepted>>res).response.roundId,
                    betIndex: betIndex
                }));
            } else {
                throw new BetFailedError();
            }
        } catch (e) {
            console.error(e);
            this.removeUserBet(betIndex, false, true);
            if (e instanceof BetsNoLongerAccepted || e instanceof UnsufficientBalanceError || e instanceof BetAlreadyExists) {
                throw e;
            }
            throw new BetFailedError();
        } finally {
            this.betActionInProgress$.next({
                ...this.betActionInProgress$.getValue(),
                [betIndex]: false
            });
        }
    };

    async cancelBet(betIndex: number): Promise<void> {
        if (this.betActionInProgress$.getValue()[betIndex]) {
            return;
        }
        try {
            this.betActionInProgress$.next({
                ...this.betActionInProgress$.getValue(),
                [betIndex]: true
            });
            const response$ = race(this.gameNsp.on<any>("betCanceled")
                    .pipe(filter((r: { payload: IRoundBetCanceled }) => r.payload.betIndex === betIndex),
                        catchError((e) => {
                            return throwError(e);
                        }),
                        map((res) => this.mapBetActionRpcResponse<IRoundBetCanceled>(res.payload, true))),
                this.gameNsp.on<any>("betCancelFailed")
                    .pipe(filter((r: { payload: IRoundBetCanceled }) => r.payload.betIndex === betIndex),
                        catchError((e) => {
                            return throwError(e);
                        }),
                        map((res) => this.mapBetActionRpcResponse<IRoundBetCanceled>(res.payload, false))))
                .pipe(timeout(120000), take(1));
            await this.gameNsp.initialized;
            await this.gameNsp.rpc("cancelBet", {
                payload: {
                    betIndex
                }
            });
            const res: IBetActionResponse<IRoundBetCanceled> = await lastValueFrom(response$);
            if (res.successful) {
                this.removeUserBet(betIndex, false, false);
            } else {
                throw new BetCancelFailedError();
            }
        } catch (e) {
            console.error(e);
            if (e instanceof BetDoesntExist) {
                throw e;
            } else {
                throw new BetCancelFailedError();
            }
        } finally {
            this.betActionInProgress$.next({
                ...this.betActionInProgress$.getValue(),
                [betIndex]: false
            });
        }
    }

    async cashOut(betIndex: number, roundId: string, split: EBetSplit, isAutoStop?: boolean): Promise<IRoundPayout | void> {
        if (this.betActionInProgress$.getValue()[betIndex]) {
            return;
        }
        try {
            this.betActionInProgress$.next({
                ...this.betActionInProgress$.getValue(),
                [betIndex]: true
            });

            const currentUserBetState: IUserBet | undefined = await firstValueFrom(this.get().pipe(map((round: IRound) => round.userBets.find(b => b.betIndex === betIndex))));
            const cashOutSplit: EBetSplit = split === EBetSplit.FirstHalf ? split : (!!currentUserBetState?.firstHalfCashedOut ? EBetSplit.SecondHalf : EBetSplit.Full);

            if (!isAutoStop) {
                await this.gameNsp.initialized;
                console.warn("SENDING STOP MESSAGE TIME: ", Date.now());
                await this.gameNsp.rpc("stop", {
                    payload: {
                        betIndex,
                        split: cashOutSplit
                    }
                });
            }

            const alreadyReceivedPayoutMsg: IBetActionResponse<IRoundCashOut> | undefined = betIndex === 0 ? this.payoutActionResultOnFirstBetIndex$.getValue() : this.payoutActionResultOnSecondBetIndex$.getValue();
            const res: IBetActionResponse<IRoundCashOut> = alreadyReceivedPayoutMsg ?? await firstValueFrom(this.subscribeCashOutActionResult(betIndex, roundId)
                .pipe(filter(Boolean), timeout(120000), take(1)));
            console.warn("PAYOUT RESPONSE TIME: ", Date.now(), "PAYOUT STATUS: ", res.successful ? "payoutSuccess" : "payoutFail",
                res);

            if (res.successful) {
                if (split === EBetSplit.FirstHalf) {
                    this.store.dispatch(firstHalfCashedOut({roundId, betIndex, amount: res.response.amount}));
                }
                return Promise.resolve(res.response);
            } else {
                throw new BetCashOutFailedError();
            }
        } catch (e) {
            console.error(e);
            if (e instanceof StopReceivedAfterGameEnd || e instanceof BetDoesntExist) {
                throw e;
            } else {
                throw new BetCashOutFailedError();
            }
        } finally {
            if (betIndex === 0) {
                this.payoutActionResultOnFirstBetIndex$.next(undefined);
            } else {
                this.payoutActionResultOnSecondBetIndex$.next(undefined);
            }
            this.betActionInProgress$.next({
                ...this.betActionInProgress$.getValue(),
                [betIndex]: false
            });
        }
    }

    addUserBet(amount: number, betIndex: number, maxMultiplier: number | undefined, roundId: string | undefined = undefined): void {
        this.store.dispatch(addUserBet({
            data: {
                amount,
                betIndex,
                maxMultiplier,
                roundId
            }
        }));
    }

    removeUserBet(betIndex: number, isCashOut: boolean = false, skipAutoplayOptionsRemoving: boolean = false): void {
        this.store.dispatch(removeUserBet({
            betIndex,
            isCashOut,
            skipAutoplayOptionsRemoving
        }));
    }

    setAutoplayOptions(data: IAutoplayOptions): void {
        this.store.dispatch(setAutoplayOptions({ options: data }));
    }

    subscribeToCashOut(): Observable<IRoundPayout> {
        return this.cashOut$.asObservable();
    }

    stopAutoplay(betIndex: number): void {
        this.store.dispatch(removeAutoplayOptions({ betIndex }));
    }
}
